import React, { useState } from 'react';
import { Card, Table, Button, Tag, Typography, Alert, Modal, Form, Select, Input, message, Space, Tabs, Checkbox } from 'antd';
import { TeamOutlined, EditOutlined, CrownOutlined, UserAddOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAdminFoundingCohortQuery,
    useUpdateAdminFoundingCohortStatusMutation,
    useGetAdminSubscriptionPlansQuery,
    useChangeAdminOrganisationPlanMutation,
    useGetAdminEligibleOrganisationsQuery,
    useBulkAddFoundingCohortMembersMutation,
    IFoundingCohortMember,
} from '../../services/nestjs-base-api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_COLORS: Record<string, string> = {
    active: 'green',
    warning: 'orange',
    grace_period: 'gold',
    graduated: 'blue',
    churned: 'red',
};

// Current Members Tab Component
const CurrentMembersTab: React.FC<{
    members: IFoundingCohortMember[];
    isLoading: boolean;
    onUpdateStatus: (member: IFoundingCohortMember) => void;
    onGraduate: (member: IFoundingCohortMember) => void;
}> = ({ members, isLoading, onUpdateStatus, onGraduate }) => {
    const [statusFilter, setStatusFilter] = useState<string>('all');

    const filteredMembers = statusFilter === 'all'
        ? members
        : members.filter(m => m.status === statusFilter);

    const stats = {
        total: members.length,
        active: members.filter(m => m.status === 'active').length,
        warning: members.filter(m => m.status === 'warning').length,
        gracePeriod: members.filter(m => m.status === 'grace_period').length,
        graduated: members.filter(m => m.status === 'graduated').length,
        churned: members.filter(m => m.status === 'churned').length,
    };

    const columns = [
        {
            title: <FormattedMessage id="foundingCohort.organization" defaultMessage="Organization" />,
            dataIndex: ['organisation', 'name'],
            key: 'organization',
        },
        {
            title: <FormattedMessage id="foundingCohort.status" defaultMessage="Status" />,
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={STATUS_COLORS[status] || 'default'}>
                    <FormattedMessage id={`foundingCohort.status.${status}`} defaultMessage={status.toUpperCase()} />
                </Tag>
            ),
        },
        {
            title: <FormattedMessage id="foundingCohort.joinedDate" defaultMessage="Joined" />,
            dataIndex: 'joinedAt',
            key: 'joinedAt',
            render: (date: string) => new Date(date).toLocaleDateString(),
        },
        {
            title: <FormattedMessage id="foundingCohort.currentMonthResponses" defaultMessage="Current Month Responses" />,
            dataIndex: 'currentMonthResponses',
            key: 'currentMonthResponses',
            render: (count: number) => (
                <Text strong style={{ color: count >= 250 ? '#52c41a' : '#ff4d4f' }}>
                    {count}
                </Text>
            ),
        },
        {
            title: <FormattedMessage id="foundingCohort.consecutiveMonthsMet" defaultMessage="Months Met" />,
            dataIndex: 'consecutiveMonthsMet',
            key: 'consecutiveMonthsMet',
        },
        {
            title: <FormattedMessage id="foundingCohort.consecutiveMonthsMissed" defaultMessage="Months Missed" />,
            dataIndex: 'consecutiveMonthsMissed',
            key: 'consecutiveMonthsMissed',
        },
        {
            title: <FormattedMessage id="foundingCohort.gracePeriodsUsed" defaultMessage="Grace Periods" />,
            dataIndex: 'gracePeriodsUsed',
            key: 'gracePeriodsUsed',
        },
        {
            title: <FormattedMessage id="common.actions" defaultMessage="Actions" />,
            key: 'actions',
            render: (_: any, record: IFoundingCohortMember) => (
                <Space size="small">
                    <Button
                        size="small"
                        icon={<EditOutlined style={{ marginRight: 4 }} />}
                        onClick={() => onUpdateStatus(record)}
                    >
                        <FormattedMessage id="foundingCohort.updateStatus" defaultMessage="Update Status" />
                    </Button>
                    {record.status === 'active' && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<CrownOutlined style={{ marginRight: 4 }} />}
                            onClick={() => onGraduate(record)}
                        >
                            <FormattedMessage id="foundingCohort.graduate" defaultMessage="Graduate" />
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <Card size="small">
                    <Text type="secondary">Total</Text>
                    <br />
                    <Text strong style={{ fontSize: 24 }}>{stats.total}</Text>
                </Card>
                <Card size="small">
                    <Text type="secondary">Active</Text>
                    <br />
                    <Text strong style={{ fontSize: 24, color: '#52c41a' }}>{stats.active}</Text>
                </Card>
                <Card size="small">
                    <Text type="secondary">Warning</Text>
                    <br />
                    <Text strong style={{ fontSize: 24, color: '#faad14' }}>{stats.warning}</Text>
                </Card>
                <Card size="small">
                    <Text type="secondary">Grace Period</Text>
                    <br />
                    <Text strong style={{ fontSize: 24, color: '#d4b106' }}>{stats.gracePeriod}</Text>
                </Card>
                <Card size="small">
                    <Text type="secondary">Graduated</Text>
                    <br />
                    <Text strong style={{ fontSize: 24, color: '#F59E0B' }}>{stats.graduated}</Text>
                </Card>
                <Card size="small">
                    <Text type="secondary">Churned</Text>
                    <br />
                    <Text strong style={{ fontSize: 24, color: '#ff4d4f' }}>{stats.churned}</Text>
                </Card>
            </div>

            <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 200, marginBottom: 16 }}
                options={[
                    { label: 'All Statuses', value: 'all' },
                    { label: 'Active', value: 'active' },
                    { label: 'Warning', value: 'warning' },
                    { label: 'Grace Period', value: 'grace_period' },
                    { label: 'Graduated', value: 'graduated' },
                    { label: 'Churned', value: 'churned' },
                ]}
            />

            <Table
                dataSource={filteredMembers}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: false,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} members`,
                }}
            />
        </>
    );
};

// Eligible Organizations Tab Component
const EligibleOrganisationsTab: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const [includeMock, setIncludeMock] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

    const { data: eligibleOrgs, isLoading, error, refetch } = useGetAdminEligibleOrganisationsQuery({
        jwtToken: user?.jwtToken || '',
        includeMock,
    });

    const [bulkAdd, { isLoading: isBulkAdding }] = useBulkAddFoundingCohortMembersMutation();

    const handleBulkAdd = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning(intl.formatMessage({ id: 'foundingCohort.selectOrgs', defaultMessage: 'Please select organizations to add' }));
            return;
        }

        try {
            const result = await bulkAdd({
                jwtToken: user?.jwtToken || '',
                organisationIds: selectedRowKeys,
            }).unwrap();

            message.success(intl.formatMessage(
                { id: 'foundingCohort.bulkAddSuccess', defaultMessage: 'Added {added} organizations, skipped {skipped}' },
                { added: result.added, skipped: result.skipped }
            ));

            if (result.errors.length > 0) {
                message.warning(intl.formatMessage(
                    { id: 'foundingCohort.bulkAddErrors', defaultMessage: 'Some errors occurred: {errors}' },
                    { errors: result.errors.join(', ') }
                ));
            }

            setSelectedRowKeys([]);
            refetch();
        } catch (error) {
            message.error(intl.formatMessage({ id: 'foundingCohort.bulkAddError', defaultMessage: 'Failed to add organizations' }));
        }
    };

    const handleSelectAll = () => {
        if (eligibleOrgs) {
            const nonMockOrgs = includeMock ? eligibleOrgs : eligibleOrgs.filter(org => !org.isMock);
            setSelectedRowKeys(nonMockOrgs.map(org => org.id));
        }
    };

    const columns = [
        {
            title: <FormattedMessage id="foundingCohort.organization" defaultMessage="Organization" />,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: <FormattedMessage id="foundingCohort.plan" defaultMessage="Current Plan" />,
            dataIndex: 'plan',
            key: 'plan',
            render: (plan: string) => (
                <Tag color={plan === 'Free' ? 'default' : 'blue'}>{plan}</Tag>
            ),
        },
        {
            title: <FormattedMessage id="foundingCohort.users" defaultMessage="Users" />,
            dataIndex: 'userCount',
            key: 'userCount',
        },
        {
            title: <FormattedMessage id="foundingCohort.created" defaultMessage="Created" />,
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleDateString(),
        },
        {
            title: <FormattedMessage id="foundingCohort.mock" defaultMessage="Mock" />,
            dataIndex: 'isMock',
            key: 'isMock',
            render: (isMock: boolean) => isMock ? (
                <Tag color="orange">Mock</Tag>
            ) : (
                <Tag color="green">Real</Tag>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys as number[]);
        },
    };

    if (error) {
        return (
            <Alert
                message="Error loading eligible organizations"
                description="Unable to fetch eligible organizations. Please try again later."
                type="error"
                showIcon
            />
        );
    }

    const nonMockCount = eligibleOrgs?.filter(org => !org.isMock).length || 0;
    const mockCount = eligibleOrgs?.filter(org => org.isMock).length || 0;

    return (
        <>
            <Alert
                message={<FormattedMessage id="foundingCohort.eligibleInfo" defaultMessage="Eligible Organizations" />}
                description={
                    <FormattedMessage
                        id="foundingCohort.eligibleDescription"
                        defaultMessage="Organizations shown below are not currently in the Founding Cohort and don't have an active subscription plan. Select organizations and click 'Add to Founding Cohort' to promote them."
                    />
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Space>
                    <Checkbox
                        checked={includeMock}
                        onChange={(e) => {
                            setIncludeMock(e.target.checked);
                            setSelectedRowKeys([]);
                        }}
                    >
                        <FormattedMessage id="foundingCohort.includeMock" defaultMessage="Include mock organizations" />
                    </Checkbox>
                    <Text type="secondary">
                        ({nonMockCount} real{includeMock && `, ${mockCount} mock`})
                    </Text>
                </Space>
                <Space>
                    <Button onClick={handleSelectAll}>
                        <FormattedMessage id="foundingCohort.selectAll" defaultMessage="Select All" />
                    </Button>
                    <Button
                        type="primary"
                        icon={<UserAddOutlined style={{ marginRight: 4 }} />}
                        onClick={handleBulkAdd}
                        loading={isBulkAdding}
                        disabled={selectedRowKeys.length === 0}
                    >
                        <FormattedMessage
                            id="foundingCohort.addToFoundingCohort"
                            defaultMessage="Add to Founding Cohort ({count})"
                            values={{ count: selectedRowKeys.length }}
                        />
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={eligibleOrgs || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                rowSelection={rowSelection}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: false,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} organizations`,
                }}
            />
        </>
    );
};

export const AdminFoundingCohortPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const [editingMember, setEditingMember] = useState<IFoundingCohortMember | null>(null);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [isGraduateModalOpen, setIsGraduateModalOpen] = useState(false);
    const [graduatingMember, setGraduatingMember] = useState<IFoundingCohortMember | null>(null);
    const [form] = Form.useForm();
    const [graduateForm] = Form.useForm();

    const { data: members, isLoading, error } = useGetAdminFoundingCohortQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: plans } = useGetAdminSubscriptionPlansQuery({
        jwtToken: user?.jwtToken || '',
    });

    const [updateStatus, { isLoading: isUpdating }] = useUpdateAdminFoundingCohortStatusMutation();
    const [changePlan, { isLoading: isChangingPlan }] = useChangeAdminOrganisationPlanMutation();

    const handleUpdateStatus = (member: IFoundingCohortMember) => {
        setEditingMember(member);
        form.setFieldsValue({
            status: member.status,
            reason: '',
        });
        setIsStatusModalOpen(true);
    };

    const handleSubmitStatus = async () => {
        try {
            const values = await form.validateFields();

            if (!editingMember) return;

            await updateStatus({
                jwtToken: user?.jwtToken || '',
                orgId: editingMember.organisationId,
                payload: values,
            }).unwrap();

            message.success(intl.formatMessage({ id: 'foundingCohort.updateSuccess', defaultMessage: 'Status updated successfully' }));
            setIsStatusModalOpen(false);
            form.resetFields();
        } catch (error) {
            message.error(intl.formatMessage({ id: 'foundingCohort.updateError', defaultMessage: 'Failed to update status' }));
        }
    };

    const handleGraduate = (member: IFoundingCohortMember) => {
        setGraduatingMember(member);
        graduateForm.resetFields();
        setIsGraduateModalOpen(true);
    };

    const handleSubmitGraduate = async () => {
        try {
            const values = await graduateForm.validateFields();

            if (!graduatingMember) return;

            await changePlan({
                jwtToken: user?.jwtToken || '',
                orgId: graduatingMember.organisationId.toString(),
                payload: {
                    planId: values.planId,
                    graduateFromFoundingCohort: true,
                },
            }).unwrap();

            message.success(intl.formatMessage({ id: 'foundingCohort.graduateSuccess', defaultMessage: 'Organization graduated successfully' }));
            setIsGraduateModalOpen(false);
            graduateForm.resetFields();
        } catch (error) {
            message.error(intl.formatMessage({ id: 'foundingCohort.graduateError', defaultMessage: 'Failed to graduate organization' }));
        }
    };

    // Filter out the founding cohort plan from the available plans for graduation
    const graduationPlans = plans?.filter(p => p.code !== 'founding_cohort') || [];

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading founding cohort"
                    description="Unable to fetch founding cohort members. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'members',
            label: (
                <span>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="foundingCohort.currentMembers" defaultMessage="Current Members" />
                </span>
            ),
            children: (
                <CurrentMembersTab
                    members={members || []}
                    isLoading={isLoading}
                    onUpdateStatus={handleUpdateStatus}
                    onGraduate={handleGraduate}
                />
            ),
        },
        {
            key: 'eligible',
            label: (
                <span>
                    <UserAddOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="foundingCohort.eligibleOrganizations" defaultMessage="Eligible Organizations" />
                </span>
            ),
            children: <EligibleOrganisationsTab />,
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <Title level={3} style={{ margin: 0, marginBottom: 16 }}>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="foundingCohort.title" defaultMessage="Founding Cohort" />
                </Title>

                <Tabs items={tabItems} />
            </Card>

            <Modal
                title={<FormattedMessage id="foundingCohort.updateStatusTitle" defaultMessage="Update Founding Cohort Status" />}
                open={isStatusModalOpen}
                onOk={handleSubmitStatus}
                onCancel={() => {
                    setIsStatusModalOpen(false);
                    form.resetFields();
                }}
                confirmLoading={isUpdating}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={<FormattedMessage id="foundingCohort.organizationName" defaultMessage="Organization" />}
                    >
                        <Input value={editingMember?.organisation.name} disabled />
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label={<FormattedMessage id="foundingCohort.newStatus" defaultMessage="New Status" />}
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Select.Option value="active">Active</Select.Option>
                            <Select.Option value="warning">Warning</Select.Option>
                            <Select.Option value="grace_period">Grace Period</Select.Option>
                            <Select.Option value="graduated">Graduated</Select.Option>
                            <Select.Option value="churned">Churned</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label={<FormattedMessage id="foundingCohort.reason" defaultMessage="Reason (optional)" />}
                    >
                        <TextArea rows={3} placeholder={intl.formatMessage({ id: 'foundingCohort.reasonPlaceholder', defaultMessage: 'Reason for status change...' })} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={
                    <span>
                        <CrownOutlined style={{ marginRight: 8, color: '#faad14' }} />
                        <FormattedMessage id="foundingCohort.graduateTitle" defaultMessage="Graduate to Paid Plan" />
                    </span>
                }
                open={isGraduateModalOpen}
                onOk={handleSubmitGraduate}
                onCancel={() => {
                    setIsGraduateModalOpen(false);
                    graduateForm.resetFields();
                }}
                confirmLoading={isChangingPlan}
                okText={<FormattedMessage id="foundingCohort.graduateConfirm" defaultMessage="Graduate Organization" />}
            >
                <Form form={graduateForm} layout="vertical">
                    <Form.Item
                        label={<FormattedMessage id="foundingCohort.organizationName" defaultMessage="Organization" />}
                    >
                        <Input value={graduatingMember?.organisation?.name} disabled />
                    </Form.Item>

                    <Form.Item
                        name="planId"
                        label={<FormattedMessage id="foundingCohort.selectPlan" defaultMessage="Select Plan" />}
                        rules={[{ required: true, message: 'Please select a plan' }]}
                    >
                        <Select placeholder="Select a plan to graduate to">
                            {graduationPlans.map(plan => (
                                <Select.Option key={plan.id} value={plan.id}>
                                    {plan.name} - {plan.currency} {(plan.priceCents / 100).toFixed(2)}/{plan.billingPeriod}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Alert
                        message={<FormattedMessage id="foundingCohort.graduateWarning" defaultMessage="This action will:" />}
                        description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li><FormattedMessage id="foundingCohort.graduateAction1" defaultMessage="Change the organization's subscription to the selected plan" /></li>
                                <li><FormattedMessage id="foundingCohort.graduateAction2" defaultMessage="Mark them as 'Graduated' in the Founding Cohort" /></li>
                                <li><FormattedMessage id="foundingCohort.graduateAction3" defaultMessage="They will lose Founding Cohort benefits" /></li>
                            </ul>
                        }
                        type="warning"
                        showIcon
                    />
                </Form>
            </Modal>
        </div>
    );
};
